/**
 *  @swagger
 *  definitions:
 *    RegattaResultOverallParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Public:
 *          description: Public of the RegattaResultOverall
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaResultOverall
 *          type: string
 *        Type:
 *          description: Type of the RegattaResultOverall
 *          $ref: '#/components/enums/RegattaResultOverallType'
 *        Filter:
 *          description: Filter of the RegattaResultOverall
 *          $ref: '#/components/enums/RegattaResultOverallFilter'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Public
 *            - Name
 *            - Type
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultOverall
 *              type: string
 *        - $ref: '#/definitions/RegattaResultOverallParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverallUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultOverall:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultOverallCreateParams'
 *        - $ref: '#/definitions/RegattaResultOverallUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultOverall:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultOverall'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaResultOverall
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaResultOverallEntries:
 *                description: RegattaResultOverallEntries of the RegattaResultOverall
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultOverallEntry'
 *              RegattaResultTables:
 *                description: RegattaResultTables of the RegattaResultOverall
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTable'
 */

// Enums
import {
  RegattaResultOverallFilter,
  RegattaResultOverallType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Filter: null | RegattaResultOverallFilter;
  Name: string;
  Public: boolean;
  Type: RegattaResultOverallType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
  RegattaResultOverallEntries?: Array<$Model<'RegattaResultOverallEntry'>>;
  RegattaResultTables?: Array<$Model<'RegattaResultTable'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Filter: {
    allowNull: true,
    type: Sequelize.ENUM(...Object.values(RegattaResultOverallFilter)),
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaResultOverallType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultOverall'> => {
  const RegattaResultOverall = stack.define(
    'RegattaResultOverall',
    fields(Sequelize),
    {
      tableName: 'RegattaResultOveralls',
    },
  );

  RegattaResultOverall.associate = (models: $ModelClasses) => {
    models.RegattaResultOverall.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaResultOverall.hasMany(
      models.RegattaResultOverallEntry,
      {
        as: 'RegattaResultOverallEntries',
        foreignKey: 'RegattaResultOverallId',
      },
    );
    models.RegattaResultOverall.belongsToMany(
      models.RegattaResultTable,
      {
        as: 'RegattaResultTables',
        foreignKey: 'RegattaResultOverallId',
        otherKey: 'RegattaResultTableId',
        through: models.RegattaResultOverallRegattaResultTable,
      },
    );
  };

  RegattaResultOverall.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultOverall;
});
